import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchNavigationTranslationsForLanguage, setPreferredLanguage, TranslationType, normalizeLanguage } from '../api/translationsApi';

// Empty translations object for fallback
export const emptyTranslations: TranslationType = {
  navigations: {
    header: {
      home: "Home",
      news: "News",
      entertainment: "Entertainment",
      sports: "Sports",
      signIn: "Sign In"
    },
    footer: {
      copyright: "© 2024 Global24. All rights reserved.",
      about: "About Us",
      contact: "Contact",
      terms: "Terms of Service",
      privacy: "Privacy Policy",
      followUs: "Follow Us",
      subscribe: "Subscribe to Newsletter"
    }
  }
};

// Add a translations cache to prevent redundant API calls
const translationsCache: Record<string, {
  timestamp: number;
  data: TranslationType;
}> = {};

// Cache expiration time - 10 minutes
const CACHE_EXPIRATION_MS = 10 * 60 * 1000;

// Track in-flight requests to prevent duplicate calls
const pendingRequests: Record<string, Promise<TranslationType>> = {};

interface TranslationContextType {
  translations: TranslationType;
  language: string;
  isLoading: boolean;
  changeLanguage: (newLanguage: string) => Promise<boolean>;
  forceReload: () => Promise<boolean>;
  getUrlLanguagePrefix: (lang: string) => string;
}

const TranslationContext = createContext<TranslationContextType>({
  translations: emptyTranslations,
  language: 'en',
  isLoading: true,
  changeLanguage: async () => false, // Return a boolean to satisfy ESLint
  forceReload: async () => false,
  getUrlLanguagePrefix: () => '/en',
});

// Generate a unique ID for tracking updates
const generateUniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Add logging to examine the structure
const processApiResponse = (data: any): TranslationType => {
  console.log('[TranslationContext] Processing API response:', {
    hasNavigations: !!data?.navigations,
    hasPrivacyInNav: !!data?.navigations?.privacy,
    hasTermsInNav: !!data?.navigations?.terms,
    hasRootPrivacy: !!data?.privacy,
    hasRootTerms: !!data?.terms,
    dataKeys: data ? Object.keys(data) : [],
    navKeys: data?.navigations ? Object.keys(data.navigations) : []
  });

  if (!data) {
    console.warn('[TranslationContext] No data received, returning empty object');
    return { navigations: {} };
  }

  // Create a new object with the navigations
  const processedData: TranslationType = {
    navigations: data.navigations || {}
  };

  // First check for privacy and terms data at root level
  if (data.privacy) {
    console.log('[TranslationContext] Found privacy at root level');
    processedData.privacy = data.privacy;
  }
  
  if (data.terms) {
    console.log('[TranslationContext] Found terms at root level');
    processedData.terms = data.terms;
  }

  // Then check if privacy and terms are in navigations and copy them to root if needed
  if (data.navigations) {
    if (data.navigations.privacy && !processedData.privacy) {
      console.log('[TranslationContext] Found privacy in navigations, promoting to root level');
      processedData.privacy = data.navigations.privacy;
    }

    if (data.navigations.terms && !processedData.terms) {
      console.log('[TranslationContext] Found terms in navigations, promoting to root level');
      processedData.terms = data.navigations.terms;
    }
  }

  // Keep any other root-level properties
  Object.keys(data).forEach(key => {
    if (key !== 'navigations' && key !== 'privacy' && key !== 'terms') {
      processedData[key] = data[key];
      console.log(`[TranslationContext] Preserving root-level property: ${key}`);
    }
  });

  console.log('[TranslationContext] Final processed data structure:', {
    hasNavigations: !!processedData.navigations,
    hasPrivacy: !!processedData.privacy,
    hasTerms: !!processedData.terms,
    rootKeys: Object.keys(processedData)
  });

  return processedData;
};

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [translations, setTranslations] = useState<TranslationType>(emptyTranslations);
  const [language, setLanguage] = useState<string>('en');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [lastLoadedLanguage, setLastLoadedLanguage] = useState<string>('');
  const [updateCounter, setUpdateCounter] = useState<number>(0);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Keep track of the latest in-progress language change to prevent race conditions
  const currentlyLoadingLanguage = useRef<string | null>(null);
  // const pendingLanguageChange = useRef<string | null>(null);
  
  // Track if we're initialized from the URL path yet
  const isInitializedRef = useRef<boolean>(false);

  // Get language from URL path
  const getLanguageFromPath = useCallback(() => {
    const path = location.pathname;
    console.log(`[TranslationContext] Detecting language from path: ${path}`);
    if (path.startsWith('/cn')) return 'cn';
    if (path.startsWith('/tw')) return 'tw';
    if (path.startsWith('/es')) return 'es';
    if (path.startsWith('/fr')) return 'fr';
    return 'en'; // Default to English
  }, [location.pathname]);

  // Convert language code to URL prefix
  const getUrlLanguagePrefix = useCallback((lang: string) => {
    if (!lang) return '/en';
    
    const langLower = lang.toLowerCase();
    if (langLower.startsWith('cn')) return '/cn';
    if (langLower.startsWith('tw')) return '/tw';
    if (langLower.startsWith('es')) return '/es';
    if (langLower.startsWith('fr')) return '/fr';
    return '/en'; // Default to English
  }, []);

  // Load translations for a specific language
  const loadTranslations = useCallback(async (lang: string): Promise<boolean> => {
    console.log(`[TranslationContext] Loading translations for language: ${lang}`);
    
    // Normalize the language code to ensure consistent API calls
    const normalizedLang = normalizeLanguage(lang);
    console.log(`[TranslationContext] Normalized language: ${normalizedLang}`);

    // Check if we already have a valid cached version
    const cachedTranslation = translationsCache[normalizedLang];
    const now = Date.now();
    
    if (cachedTranslation && (now - cachedTranslation.timestamp < CACHE_EXPIRATION_MS)) {
      console.log(`[TranslationContext] Using cached translations for ${normalizedLang} (age: ${(now - cachedTranslation.timestamp) / 1000}s)`);
      
      // Store the loading language to check for conflicts later
      // const requestId = generateUniqueId();
      currentlyLoadingLanguage.current = normalizedLang;
      
      try {
        // Set loading state
        setIsLoading(true);
        
        // Use cached data
        const processedData = cachedTranslation.data;
        
        // Add a unique update ID
        const freshTranslations = {
          ...processedData,
          _updateId: generateUniqueId()
        };
        
        console.log(`[TranslationContext] Setting cached translations for ${normalizedLang}`);
        
        // Update the translation data
        setTranslations(freshTranslations);
        setLastLoadedLanguage(normalizedLang);
        setUpdateCounter(prev => prev + 1); // Increment counter to force re-renders
        
        return true;
      } finally {
        setIsLoading(false);
        currentlyLoadingLanguage.current = null;
      }
    }
    
    // Check if there's already a request in flight for this language
    if (pendingRequests[normalizedLang] !== undefined) {
      console.log(`[TranslationContext] Reusing in-flight request for ${normalizedLang}`);
      
      // Set loading state
      setIsLoading(true);
      currentlyLoadingLanguage.current = normalizedLang;
      
      try {
        // Wait for the existing request to complete
        const data = await pendingRequests[normalizedLang];
        
        // Add a unique update ID
        const freshTranslations = {
          ...data,
          _updateId: generateUniqueId()
        };
        
        // Update the translation data
        setTranslations(freshTranslations);
        setLastLoadedLanguage(normalizedLang);
        setUpdateCounter(prev => prev + 1);
        
        return true;
      } catch (error) {
        console.error(`[TranslationContext] Error from shared request for ${normalizedLang}:`, error);
        return false;
      } finally {
        if (currentlyLoadingLanguage.current === normalizedLang) {
          setIsLoading(false);
          currentlyLoadingLanguage.current = null;
        }
      }
    }
    
    // Store the loading language to check for conflicts later
    const requestId = generateUniqueId();
    
    // Start loading
    setIsLoading(true);
    currentlyLoadingLanguage.current = normalizedLang;
    
    // Create a shared promise for this request
    const requestPromise = (async () => {
      try {
        console.log(`[TranslationContext] Starting request ${requestId} for language ${normalizedLang}`);
        const data = await fetchNavigationTranslationsForLanguage(normalizedLang);
        
        // Process the data
        if (data && data.navigations) {
          console.log(`[TranslationContext] Processing data for ${normalizedLang} (request ${requestId})`);
          
          // Process the data to ensure privacy/terms are at root level
          const processedData = processApiResponse(data);
          
          // Cache the response
          translationsCache[normalizedLang] = {
            timestamp: Date.now(),
            data: processedData
          };
          
          return processedData;
        } else {
          console.error(`[TranslationContext] Invalid data format for ${normalizedLang}, using fallback translations`);
          // Use fallback translations to prevent UI issues
          return emptyTranslations;
        }
      } catch (error) {
        console.error(`[TranslationContext] Error loading translations for ${normalizedLang}:`, error);
        throw error;
      } finally {
        // Remove from pending requests
        delete pendingRequests[normalizedLang];
      }
    })();
    
    // Store the promise for potential reuse
    pendingRequests[normalizedLang] = requestPromise;
    
    try {
      // Wait for the request to complete
      const processedData = await requestPromise;
      
      // Check if we've been superseded by another request
      if (currentlyLoadingLanguage.current !== normalizedLang) {
        console.log(`[TranslationContext] Request ${requestId} superseded: current language is now ${currentlyLoadingLanguage.current}`);
        return false;
      }
      
      // Force a complete refresh by creating a new reference with a unique update ID
      const freshTranslations = {
        ...processedData,
        _updateId: generateUniqueId()
      };
      
      console.log(`[TranslationContext] Setting translations for ${normalizedLang} (request ${requestId})`);
      
      // Update the translation data
      setTranslations(freshTranslations);
      setLastLoadedLanguage(normalizedLang);
      setUpdateCounter(prev => prev + 1); // Increment counter to force re-renders
      
      console.log(`[TranslationContext] Translations loaded successfully for ${normalizedLang} (request ${requestId})`);
      return true;
    } catch (error) {
      console.error(`[TranslationContext] Error loading translations for ${normalizedLang}:`, error);
      
      // Use fallback translations to prevent UI issues
      setTranslations({
        ...emptyTranslations,
        _updateId: generateUniqueId()
      });
      return false; // Return failure to indicate error
    } finally {
      if (currentlyLoadingLanguage.current === normalizedLang) {
        console.log(`[TranslationContext] Cleaning up request ${requestId} for ${normalizedLang}`);
        setIsLoading(false);
        currentlyLoadingLanguage.current = null;
      } else {
        console.log(`[TranslationContext] Skipping cleanup for request ${requestId} (${normalizedLang}) as current language is now ${currentlyLoadingLanguage.current}`);
      }
    }
  }, []);

  // Check URL path on location change and update language if needed
  useEffect(() => {
    const pathLanguage = getLanguageFromPath();
    
    // Log path change for debugging
    console.log(`[TranslationContext] URL path: ${location.pathname}, detected language: ${pathLanguage}`);
    
    // Skip during initial render as this is handled by the initialization effect
    if (!isInitializedRef.current) {
      console.log(`[TranslationContext] Skipping path change during initialization`);
      return;
    }
    
    // Only update language state when different from current
    if (pathLanguage !== language) {
      console.log(`[TranslationContext] Updating language from ${language} to ${pathLanguage}`);
      
      // Update UI state immediately
      setLanguage(pathLanguage);
      setIsLoading(true);
      
      // Trigger translations loading
      loadTranslations(pathLanguage)
        .then(success => {
          console.log(`[TranslationContext] Translation load from path change completed: ${success}`);
        })
        .catch(err => {
          console.error(`[TranslationContext] Error loading translations for ${pathLanguage}:`, err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [location.pathname, getLanguageFromPath, language, loadTranslations]);

  // Initial load of translations
  useEffect(() => {
    // Skip if we're already initialized
    if (isInitializedRef.current) {
      console.log(`[TranslationContext] Skipping initialization as already initialized`);
      return;
    }
    
    const initialLanguage = getLanguageFromPath();
    console.log(`[TranslationContext] Initial language from path: ${initialLanguage}`);
    
    // Set language immediately
    setLanguage(initialLanguage);
    setIsLoading(true);
    
    // Load initial translations - use a smaller scope async function to avoid race conditions
    (async () => {
      try {
        console.log(`[TranslationContext] Initial load of translations for ${initialLanguage}`);
        
        // Mark as initialized early to prevent double-loading
        isInitializedRef.current = true;
        
        const success = await loadTranslations(initialLanguage);
        
        if (success) {
          console.log(`[TranslationContext] Initialization completed with language: ${initialLanguage}`);
          
          // Set a short timeout to let React render with the new translations
          setTimeout(() => {
            // Force a re-render by updating the counter
            setUpdateCounter(prev => prev + 1);
          }, 100);
        } else {
          console.error(`[TranslationContext] Failed to load initial translations for ${initialLanguage}, will retry`);
          // If the initial load fails, retry once
          setTimeout(async () => {
            console.log(`[TranslationContext] Retrying initialization for ${initialLanguage}`);
            const retrySuccess = await loadTranslations(initialLanguage);
            if (retrySuccess) {
              console.log(`[TranslationContext] Retry successful for ${initialLanguage}`);
              setUpdateCounter(prev => prev + 1);
            } else {
              console.error(`[TranslationContext] Retry failed for ${initialLanguage}, using fallback`);
            }
          }, 1000);
        }
      } catch (error) {
        console.error(`[TranslationContext] Error during initialization:`, error);
      } finally {
        // Ensure loading state is cleared after initialization
        setIsLoading(false);
      }
    })();
    
    // Add a safety timeout to ensure loading state is cleared even if something goes wrong
    const safetyTimeoutId = setTimeout(() => {
      if (isLoading) {
        console.log(`[TranslationContext] Safety timeout reached, clearing loading state`);
        setIsLoading(false);
      }
    }, 5000);
    
    return () => clearTimeout(safetyTimeoutId);
  }, [getLanguageFromPath, loadTranslations, isLoading]);

  // Change language function with redirect
  const changeLanguage = useCallback(async (newLanguage: string): Promise<boolean> => {
    console.log(`[TranslationContext] Changing language to: ${newLanguage}`);
    
    // Normalize the language for consistency
    const normalizedLang = normalizeLanguage(newLanguage);
    
    // Get current route parts
    const currentPath = location.pathname;
    const currentQuery = location.search;
    
    // Check if we're already on the correct language path
    // This prevents infinite loops when redirecting to the same URL
    if (currentPath.startsWith(`/${normalizedLang}/`) || currentPath === `/${normalizedLang}`) {
      console.log(`[TranslationContext] Already on the correct language path (${currentPath}), skipping redirect`);
      // Still update the state and load translations
      setLanguage(normalizedLang);
      return await loadTranslations(normalizedLang);
    }
    
    // Set preferred language cookie immediately
    try {
      const result = await setPreferredLanguage(normalizedLang);
      console.log(`[TranslationContext] Server response for language change to ${normalizedLang}:`, result);
    } catch (error) {
      // Don't flood the console with cookie warnings
      console.warn(`[TranslationContext] Error setting preferred language cookie, continuing without cookie`);
      // Continue anyway as this is not critical
    }
    
    // Determine the new URL path with correct language prefix
    // const currentPrefix = getUrlLanguagePrefix(language);
    const newPrefix = getUrlLanguagePrefix(normalizedLang);
    
    // Extract path without language prefix
    let pathWithoutLang = currentPath;

    // Fix for multiple language prefixes in path (e.g., /es/en/about)
    // Strip all language prefixes using regex to get the actual content path
    const languagePrefixPattern = /^\/(en|cn|tw|es|fr)(\/|$)/;
    let cleanPath = pathWithoutLang;

    // Remove the language prefixes recursively
    while (languagePrefixPattern.test(cleanPath)) {
      cleanPath = cleanPath.replace(languagePrefixPattern, '/');
      
      // Edge case: if we end up with // at the start, fix it
      if (cleanPath.startsWith('//')) {
        cleanPath = cleanPath.substring(1);
      }
    }

    // Special case: if it's just a language prefix without content, make it empty
    if (cleanPath === '/') {
      cleanPath = '';
    }

    pathWithoutLang = cleanPath;

    // Ensure path starts with a slash
    if (!pathWithoutLang.startsWith('/')) {
      pathWithoutLang = '/' + pathWithoutLang;
    }
    
    // If path is empty or just '/', set to '/home'
    if (pathWithoutLang === '' || pathWithoutLang === '/') {
      pathWithoutLang = '/home';
    }
    
    // Construct the new full path
    const newPath = `${newPrefix}${pathWithoutLang}${currentQuery}`;
    
    console.log(`[TranslationContext] Redirecting from ${currentPath} to ${newPath}`);
    
    // Always update the language state immediately for UI responsiveness
    setLanguage(normalizedLang);
    
    // Load the translations
    try {
      // Set loading state to provide visual feedback
      setIsLoading(true);
      
      const success = await loadTranslations(normalizedLang);
      console.log(`[TranslationContext] Language change to ${normalizedLang} completed: ${success}`);
      
      // Force a render by incrementing counter
      setUpdateCounter(prev => prev + 1);
      
      // Force navigation to the new path to apply language change throughout the app
      setTimeout(() => {
        navigate(newPath, { replace: true });
      
        // Broadcast language change event
        window.dispatchEvent(new CustomEvent('languageChanged', { 
          detail: { language: normalizedLang, success } 
        }));
      }, 100); // Small delay to ensure state updates are processed
      
      // Use localStorage to persist the language preference
      localStorage.setItem('preferredLanguage', normalizedLang);
      
      console.log(`[TranslationContext] Language successfully changed to ${normalizedLang}`);
      return success;
    } catch (error) {
      console.error(`[TranslationContext] Error changing language to ${normalizedLang}:`, error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [language, loadTranslations, location.pathname, location.search, getUrlLanguagePrefix, navigate]);

  // Force reload translations
  const forceReload = useCallback(async (): Promise<boolean> => {
    console.log(`[TranslationContext] Force reloading translations for: ${language}`);
    
    // Always load translations, skip cache
    return loadTranslations(language);
  }, [language, loadTranslations]);

  // Provide context value
  const contextValue = {
    translations,
    language,
    isLoading,
    changeLanguage,
    forceReload,
    getUrlLanguagePrefix
  };

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
};

// Custom hook to use the translations context
export const useTranslations = () => useContext(TranslationContext); 